import type { LeadFieldsResponse, PublishLeadFieldsBody } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import type { Translate } from "../../i18n/locale";
import { SECTION_TITLES, SYSTEM_FIELD_LABELS } from "../../leads/card/card-fields";

// La bozza di «Campi della scheda» (piano M2 Task 20, D6), senza interfaccia: le modifiche della pagina sono un elenco
// di `DraftEdit` che si applica, ogni volta da capo, al modello pubblicato (`GET /api/settings/lead-fields`). Dal
// risultato nascono il corpo di una sola pubblicazione (`POST /api/settings/lead-fields/publish`), le frasi di «Cosa
// cambia per il team» (una per modifica: il loro numero è quello della barra) e ciò che impedisce di salvare. Tenere le
// modifiche e non il risultato permette di rimetterle sopra una versione più nuova dopo un conflitto (questione aperta
// 108). Le regole sono quelle del server (`validateCardModel`, `publishLeadFields`), che resta l'autorità: le sezioni del
// modello che si spostano (due; tre nei modelli pubblicati prima del 1° ottobre 2026, con «Territorio»), cellulare e
// fisso sempre visibili con la loro sezione, i campi di sistema fermi nella loro sezione (provincia, regione e nazione
// comprese: si spostano e si nascondono, ma vengono dalla città),
// il tipo che cambia soltanto finché nessun Lead ha un valore (`usedBy` a 0; il server conta anche i Lead archiviati o
// uniti, quindi può rifiutare lo stesso), una voce pubblicata che si nasconde e non si toglie, un campo archiviato che
// esce dalla scheda. Una modifica che queste regole non ammettono non cambia nulla.

export type SectionKey = "contact" | "salon" | "territory";
export type FieldType = "number" | "currency_eur" | "choice" | "text";

export type DraftEdit =
  | { kind: "moveSection"; key: SectionKey; direction: "up" | "down" }
  | { kind: "toggleSection"; key: SectionKey; visible: boolean }
  | { kind: "moveField"; ref: string; direction: "up" | "down" }
  /** Soltanto campi dell'azienda; anche uno che oggi non sta nella scheda. */
  | { kind: "moveFieldToSection"; ref: string; section: SectionKey }
  | { kind: "toggleField"; ref: string; visible: boolean }
  | { kind: "createField"; key: string; label: string; type: FieldType; section: SectionKey; options: { id: string; label: string }[] }
  /** Soltanto con `usedBy === 0` (o per un campo nuovo); da scelta a un altro tipo toglie le voci. */
  | { kind: "changeType"; key: string; type: FieldType }
  | { kind: "renameField"; key: string; label: string }
  | { kind: "addOption"; key: string; option: { id: string; label: string } }
  | { kind: "renameOption"; key: string; optionId: string; label: string }
  | { kind: "hideOption"; key: string; optionId: string; hidden: boolean }
  /** Soltanto una voce aggiunta in questa bozza: una voce pubblicata si nasconde, non si toglie. */
  | { kind: "removeOption"; key: string; optionId: string }
  /** Un campo pubblicato si archivia; un campo nuovo della bozza sparisce. */
  | { kind: "archiveField"; key: string };

/** Le sezioni che un campo dell'azienda può scegliere: quelle del modello, nel suo ordine («Territorio» soltanto se c'è). */
export function sectionKeysOf(state: Pick<DraftState, "sections">): SectionKey[] {
  return state.sections.map((section) => section.key);
}
export const FIELD_TYPES: readonly FieldType[] = ["number", "currency_eur", "choice", "text"];
/** Il telefono è il solo dato obbligatorio di un Lead: cellulare e fisso non si nascondono (`PROTECTED_FIELD_REFS`). */
export const PROTECTED_REFS: ReadonlySet<string> = new Set(["system:mobile", "system:landline"]);
/** La sezione dei telefoni protetti: non si nasconde nemmeno lei. */
export const LOCKED_SECTION: SectionKey = "contact";
/** I limiti del contratto (`publishLeadFieldsBodySchema`). */
export const LIMITS = { label: 80, newFields: 20, options: 50 } as const;

const FIELD_PREFIX = "field:";
const FIELD_KEY = /^[a-z][a-z0-9_]{1,40}$/;
const OPTION_ID = /^[a-z0-9_]{1,40}$/;

export const TYPE_NAME = {
  number: "leadFields.types.number",
  currency_eur: "leadFields.types.currency_eur",
  choice: "leadFields.types.choice",
  text: "leadFields.types.text",
} as const satisfies Record<FieldType, MessageKey>;
export const TYPE_IN_TEXT = {
  number: "leadFields.typesInText.number",
  currency_eur: "leadFields.typesInText.currency_eur",
  choice: "leadFields.typesInText.choice",
  text: "leadFields.typesInText.text",
} as const satisfies Record<FieldType, MessageKey>;

export type DraftOption = {
  id: string;
  label: string;
  hidden: boolean;
  /** Il nome con cui la voce è arrivata (pubblicato o scritto all'aggiunta): resta fermo mentre lo si cambia. */
  originalLabel: string;
  isNew: boolean;
};

export type DraftField = {
  key: string;
  ref: string;
  /** Com'è scritto nella casella, spazi compresi: si pulisce soltanto nel corpo e nei confronti. */
  label: string;
  originalLabel: string;
  type: FieldType;
  options: DraftOption[];
  archived: boolean;
  /** I Lead attivi con un valore (`usedBy` del server); 0 per un campo nuovo. */
  usedBy: number;
  isNew: boolean;
};

export type DraftSection = { key: SectionKey; visible: boolean; fields: Array<{ ref: string; visible: boolean }> };

export type DraftState = {
  version: number;
  sections: DraftSection[];
  /** Ogni campo dell'azienda per chiave: quelli pubblicati (anche archiviati) e poi quelli nuovi, in quest'ordine. */
  fields: Map<string, DraftField>;
  /** I campi archiviati, già pubblicati o archiviati in questa bozza. */
  archived: DraftField[];
  /** I campi dell'azienda non archiviati che il modello non mostra. */
  outside: DraftField[];
};

/** Un nome come lo salva il server: spazi ripetuti uniti, niente spazi ai lati. */
export function cleanLabel(label: string): string {
  return label.replace(/\s+/g, " ").trim();
}

const sameName = (label: string) => cleanLabel(label).toLocaleLowerCase("it");

export const keyOfRef = (ref: string): string | null => (ref.startsWith(FIELD_PREFIX) ? ref.slice(FIELD_PREFIX.length) : null);
export const refOfKey = (key: string) => `${FIELD_PREFIX}${key}`;

function cloneOptions(options: LeadFieldsResponse["definitions"][number]["options"]): DraftOption[] {
  return options.map((option) => ({ id: option.id, label: option.label, hidden: option.hidden, originalLabel: option.label, isNew: false }));
}

function stateOf(base: LeadFieldsResponse): DraftState {
  const fields = new Map<string, DraftField>();
  for (const definition of base.definitions) {
    fields.set(definition.key, {
      key: definition.key,
      ref: refOfKey(definition.key),
      label: definition.label,
      originalLabel: definition.label,
      type: definition.type,
      options: cloneOptions(definition.options),
      archived: definition.archived,
      usedBy: definition.usedBy,
      isNew: false,
    });
  }
  return {
    version: base.version,
    sections: base.model.sections.map((section) => ({ key: section.key, visible: section.visible, fields: section.fields.map((field) => ({ ...field })) })),
    fields,
    archived: [],
    outside: [],
  };
}

function sectionOf(state: DraftState, ref: string): DraftSection | undefined {
  return state.sections.find((section) => section.fields.some((field) => field.ref === ref));
}

function swap<T>(list: T[], index: number, direction: "up" | "down"): void {
  const target = direction === "up" ? index - 1 : index + 1;
  if (index < 0 || target < 0 || target >= list.length) return;
  const item = list[index] as T;
  list[index] = list[target] as T;
  list[target] = item;
}

/** Un campo dell'azienda che si può ancora cambiare: esiste e non è archiviato. */
function editableField(state: DraftState, key: string): DraftField | undefined {
  const field = state.fields.get(key);
  return field && !field.archived ? field : undefined;
}

function apply(state: DraftState, base: LeadFieldsResponse, edit: DraftEdit): void {
  switch (edit.kind) {
    case "moveSection":
      swap(state.sections, state.sections.findIndex((section) => section.key === edit.key), edit.direction);
      return;
    case "toggleSection": {
      const section = state.sections.find((candidate) => candidate.key === edit.key);
      if (section && (edit.visible || edit.key !== LOCKED_SECTION)) section.visible = edit.visible;
      return;
    }
    case "moveField": {
      const section = sectionOf(state, edit.ref);
      if (section) swap(section.fields, section.fields.findIndex((field) => field.ref === edit.ref), edit.direction);
      return;
    }
    case "moveFieldToSection": {
      const key = keyOfRef(edit.ref);
      const target = state.sections.find((section) => section.key === edit.section);
      if (key === null || !editableField(state, key) || !target) return;
      const current = sectionOf(state, edit.ref);
      if (current === target) return;
      const entry = current?.fields.find((field) => field.ref === edit.ref) ?? { ref: edit.ref, visible: true };
      if (current) current.fields = current.fields.filter((field) => field.ref !== edit.ref);
      target.fields.push(entry);
      return;
    }
    case "toggleField": {
      if (!edit.visible && PROTECTED_REFS.has(edit.ref)) return;
      const entry = sectionOf(state, edit.ref)?.fields.find((field) => field.ref === edit.ref);
      if (entry) entry.visible = edit.visible;
      return;
    }
    case "createField": {
      const target = state.sections.find((section) => section.key === edit.section);
      if (!target || state.fields.has(edit.key) || !FIELD_KEY.test(edit.key) || !FIELD_TYPES.includes(edit.type)) return;
      const options = edit.type === "choice" ? edit.options.map((option) => ({ ...option, hidden: false, originalLabel: option.label, isNew: true })) : [];
      state.fields.set(edit.key, {
        key: edit.key,
        ref: refOfKey(edit.key),
        label: edit.label,
        originalLabel: cleanLabel(edit.label),
        type: edit.type,
        options,
        archived: false,
        usedBy: 0,
        isNew: true,
      });
      target.fields.push({ ref: refOfKey(edit.key), visible: true });
      return;
    }
    case "changeType": {
      const field = editableField(state, edit.key);
      if (!field || !FIELD_TYPES.includes(edit.type) || field.type === edit.type) return;
      if (!field.isNew && field.usedBy > 0) return;
      field.type = edit.type;
      if (edit.type !== "choice") {
        field.options = [];
      } else if (!field.isNew) {
        // Tornando al tipo pubblicato tornano anche le voci pubblicate.
        const published = base.definitions.find((definition) => definition.key === field.key);
        field.options = published?.type === "choice" ? cloneOptions(published.options) : [];
      }
      return;
    }
    case "renameField": {
      const field = editableField(state, edit.key);
      if (field) field.label = edit.label;
      return;
    }
    case "addOption": {
      const field = editableField(state, edit.key);
      if (!field || field.type !== "choice" || field.options.length >= LIMITS.options) return;
      if (!OPTION_ID.test(edit.option.id) || field.options.some((option) => option.id === edit.option.id)) return;
      field.options.push({ id: edit.option.id, label: edit.option.label, hidden: false, originalLabel: cleanLabel(edit.option.label), isNew: true });
      return;
    }
    case "renameOption": {
      const option = editableField(state, edit.key)?.options.find((candidate) => candidate.id === edit.optionId);
      if (option) option.label = edit.label;
      return;
    }
    case "hideOption": {
      const option = editableField(state, edit.key)?.options.find((candidate) => candidate.id === edit.optionId);
      if (option) option.hidden = edit.hidden;
      return;
    }
    case "removeOption": {
      const field = editableField(state, edit.key);
      if (field) field.options = field.options.filter((option) => !(option.id === edit.optionId && option.isNew));
      return;
    }
    case "archiveField": {
      const field = editableField(state, edit.key);
      if (!field) return;
      const section = sectionOf(state, field.ref);
      if (section) section.fields = section.fields.filter((entry) => entry.ref !== field.ref);
      if (field.isNew) state.fields.delete(field.key);
      else field.archived = true;
      return;
    }
  }
}

/** Il modello pubblicato con sopra le modifiche, nell'ordine in cui sono state fatte. */
export function buildDraft(base: LeadFieldsResponse, edits: readonly DraftEdit[]): DraftState {
  const state = stateOf(base);
  for (const edit of edits) apply(state, base, edit);
  const shown = new Set(state.sections.flatMap((section) => section.fields.map((field) => field.ref)));
  const all = [...state.fields.values()];
  state.archived = all.filter((field) => field.archived);
  state.outside = all.filter((field) => !field.archived && !shown.has(field.ref));
  return state;
}

/** Le voci come le vuole il server, per confrontarle con quelle pubblicate. */
function optionsBody(options: readonly DraftOption[]) {
  return options.map((option) => ({ id: option.id, label: cleanLabel(option.label), hidden: option.hidden }));
}

function bodyOf(base: LeadFieldsResponse, state: DraftState): PublishLeadFieldsBody {
  const create: PublishLeadFieldsBody["create"] = [];
  const update: PublishLeadFieldsBody["update"] = [];
  for (const field of state.fields.values()) {
    if (field.isNew) {
      const options = field.type === "choice" ? field.options.filter((option) => !option.hidden).map((option) => ({ id: option.id, label: cleanLabel(option.label) })) : [];
      create.push({ key: field.key, label: cleanLabel(field.label), type: field.type, options });
      continue;
    }
    const published = base.definitions.find((definition) => definition.key === field.key);
    if (!published) continue;
    const change: PublishLeadFieldsBody["update"][number] = { key: field.key };
    const label = cleanLabel(field.label);
    if (label !== published.label) change.label = label;
    if (field.type !== published.type) change.type = field.type;
    if (field.type === "choice") {
      const options = optionsBody(field.options);
      const before = published.type === "choice" ? published.options.map(({ id, label: name, hidden }) => ({ id, label: name, hidden })) : [];
      if (JSON.stringify(options) !== JSON.stringify(before)) change.options = options;
    }
    if (field.archived !== published.archived) change.archived = field.archived;
    if (Object.keys(change).length > 1) update.push(change);
  }
  return {
    baseVersion: base.version,
    create,
    update,
    model: { sections: state.sections.map((section) => ({ key: section.key, visible: section.visible, fields: section.fields.map((field) => ({ ...field })) })) },
  };
}

/** Il nome di un campo da mostrare: dai cataloghi per un campo di sistema, quello della bozza per un campo dell'azienda. */
export function fieldName(state: DraftState, ref: string, t: Translate): string {
  const system = SYSTEM_FIELD_LABELS[ref];
  if (system) return t(system);
  const key = keyOfRef(ref);
  const field = key === null ? undefined : state.fields.get(key);
  return field ? cleanLabel(field.label) || field.originalLabel : ref;
}

export function sectionName(key: SectionKey, t: Translate): string {
  return t(SECTION_TITLES[key]);
}

type Placement = Map<string, { section: SectionKey; visible: boolean; index: number }>;

function placementOf(sections: readonly DraftSection[] | LeadFieldsResponse["model"]["sections"]): Placement {
  const placement: Placement = new Map();
  for (const section of sections) section.fields.forEach((field, index) => placement.set(field.ref, { section: section.key, visible: field.visible, index }));
  return placement;
}

/** Ciò che cambia rispetto alla versione pubblicata, a parole: una frase per modifica. */
function summaryOf(base: LeadFieldsResponse, state: DraftState, t: Translate): string[] {
  const sentences: string[] = [];
  const before = base.model.sections;
  const after = state.sections;

  if (before.map((section) => section.key).join() !== after.map((section) => section.key).join()) {
    sentences.push(t("leadFields.changes.sectionOrder", { order: after.map((section) => sectionName(section.key, t)).join(", ") }));
  }
  for (const section of after) {
    const was = before.find((candidate) => candidate.key === section.key);
    if (was && was.visible !== section.visible) {
      sentences.push(t(section.visible ? "leadFields.changes.sectionShown" : "leadFields.changes.sectionHidden", { section: sectionName(section.key, t) }));
    }
  }

  const placedBefore = placementOf(before);
  const placedAfter = placementOf(after);
  // I campi nell'ordine della scheda pubblicata, poi quelli pubblicati che la scheda non mostrava.
  const refs = [...placedBefore.keys()];
  for (const definition of base.definitions) if (!placedBefore.has(refOfKey(definition.key))) refs.push(refOfKey(definition.key));

  for (const ref of refs) {
    const key = keyOfRef(ref);
    const field = key === null ? undefined : state.fields.get(key);
    const published = key === null ? undefined : base.definitions.find((definition) => definition.key === key);
    if (field && published && !published.archived) {
      const name = fieldName(state, ref, t);
      if (cleanLabel(field.label) !== published.label) sentences.push(t("leadFields.changes.renamed", { before: published.label, after: name }));
      if (field.type !== published.type) {
        const dropsOptions = published.type === "choice" && published.options.length > 0;
        sentences.push(t(dropsOptions ? "leadFields.changes.retypedDropsOptions" : "leadFields.changes.retyped", { field: name, type: t(TYPE_IN_TEXT[field.type]) }));
      }
      if (field.type === "choice") {
        const old = new Map((published.type === "choice" ? published.options : []).map((option) => [option.id, option]));
        for (const option of field.options) {
          const label = cleanLabel(option.label);
          const was = old.get(option.id);
          if (!was) {
            sentences.push(t("leadFields.changes.optionAdded", { field: name, option: label }));
            continue;
          }
          if (label !== was.label) sentences.push(t("leadFields.changes.optionRenamed", { field: name, before: was.label, after: label }));
          if (option.hidden !== was.hidden) sentences.push(t(option.hidden ? "leadFields.changes.optionHidden" : "leadFields.changes.optionShown", { field: name, option: label }));
        }
      }
      if (field.archived) {
        sentences.push(t("leadFields.changes.archived", { field: name }));
        continue;
      }
    }
    const was = placedBefore.get(ref);
    const now = placedAfter.get(ref);
    if (!now) continue;
    const name = fieldName(state, ref, t);
    if (was?.section !== now.section) sentences.push(t("leadFields.changes.movedTo", { field: name, section: sectionName(now.section, t) }));
    if (was && was.visible !== now.visible) sentences.push(t(now.visible ? "leadFields.changes.fieldShown" : "leadFields.changes.fieldHidden", { field: name }));
    // Un campo che la scheda pubblicata non mostrava (fuori dalla scheda) e che entra nascosto: anche questo si dice.
    else if (!was && !now.visible) sentences.push(t("leadFields.changes.enteredHidden", { field: name }));
  }

  for (const field of state.fields.values()) {
    if (!field.isNew) continue;
    const section = placedAfter.get(field.ref)?.section;
    if (!section) continue;
    const params = { field: cleanLabel(field.label), type: t(TYPE_IN_TEXT[field.type]), section: sectionName(section, t) };
    const options = field.options.filter((option) => !option.hidden).map((option) => cleanLabel(option.label));
    sentences.push(field.type === "choice" && options.length > 0 ? t("leadFields.changes.createdChoice", { ...params, options: options.join(", ") }) : t("leadFields.changes.created", params));
    if (placedAfter.get(field.ref)?.visible === false) sentences.push(t("leadFields.changes.enteredHidden", { field: params.field }));
  }

  // L'ordine dei campi che stanno nella stessa sezione prima e dopo.
  for (const section of after) {
    const common = (list: ReadonlyArray<{ ref: string }>) =>
      list.filter((field) => placedBefore.get(field.ref)?.section === section.key && placedAfter.get(field.ref)?.section === section.key).map((field) => field.ref);
    const was = before.find((candidate) => candidate.key === section.key);
    if (was && common(was.fields).join() !== common(section.fields).join()) {
      sentences.push(t("leadFields.changes.fieldOrder", { section: sectionName(section.key, t) }));
    }
  }
  return sentences;
}

/** Il primo motivo che impedisce di salvare, a parole; `null` se si può salvare. */
function problemOf(base: LeadFieldsResponse, state: DraftState, t: Translate): string | null {
  const names = new Set<string>();
  for (const field of state.fields.values()) {
    if (field.archived) continue;
    const label = cleanLabel(field.label);
    if (label === "") return t("leadFields.problems.noName");
    if (names.has(sameName(label))) return t("leadFields.problems.sameName", { field: label });
    names.add(sameName(label));
    if (field.type !== "choice") continue;
    const publishedType = base.definitions.find((definition) => definition.key === field.key)?.type;
    // Un campo a scelta che nasce ora, o che lo diventa ora, ha bisogno di voci; «Gestionale» senza voci di oggi no.
    if (field.options.length === 0 && (field.isNew || publishedType !== "choice")) return t("leadFields.problems.noOptions", { field: label });
    const options = new Set<string>();
    for (const option of field.options) {
      const name = cleanLabel(option.label);
      if (name === "") return t("leadFields.problems.noOptionName", { field: label });
      if (options.has(sameName(name))) return t("leadFields.problems.sameOption", { field: label, option: name });
      options.add(sameName(name));
    }
  }
  return null;
}

/**
 * Le modifiche applicate al modello pubblicato: il corpo della pubblicazione, il riepilogo «Cosa cambia per il team»
 * (vuoto se non cambia niente), il primo problema che impedisce di salvare e la bozza da mostrare.
 */
export function applyDraft(
  base: LeadFieldsResponse,
  edits: readonly DraftEdit[],
  t: Translate,
): { body: PublishLeadFieldsBody; summary: readonly string[]; problem: string | null; state: DraftState } {
  const state = buildDraft(base, edits);
  return { body: bodyOf(base, state), summary: summaryOf(base, state, t), problem: problemOf(base, state, t), state };
}

/**
 * La larghezza della casella del nome di un campo: quanto il nome scritto più un margine, in caratteri, da 18 a 44
 * (decisione del 1° ottobre 2026: «Addetti (titolare/i inclusi)» si leggeva tagliato). Cresce mentre si scrive. Il
 * carattere `ch` è la larghezza dello zero, un po' più larga di una lettera media: il margine copre le maiuscole; i 4
 * caratteri in più coprono il riempimento della casella.
 */
export function nameInputWidth(label: string): string {
  const chars = Math.min(Math.max(label.length + 2, 18), 44);
  return `${chars + 4}ch`;
}

/** Un nome scritto a mano come identificativo: minuscole senza accenti, il resto diventa «_». */
function slug(label: string): string {
  return label
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const trimTo = (value: string, length: number) => value.slice(0, length).replace(/_+$/, "");

function unique(root: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(root)) return root;
  for (let n = 2; ; n += 1) {
    const candidate = `${root}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
}

/** La chiave stabile di un campo nuovo dal suo nome («Colore preferito» → `colore_preferito`), mai una già presa. */
export function fieldKeyFor(label: string, taken: Iterable<string>): string {
  let root = trimTo(slug(label), 34);
  if (root === "") root = "campo";
  else if (!/^[a-z]/.test(root) || root.length < 2) root = trimTo(`campo_${root}`, 34);
  return unique(root, taken);
}

/** L'id stabile di una voce nuova dal suo nome («Agenda di carta» → `agenda_di_carta`), mai uno già preso nel campo. */
export function optionIdFor(label: string, taken: Iterable<string>): string {
  return unique(trimTo(slug(label), 34) || "voce", taken);
}
